import { useState } from 'react'

import type { MediaAsset } from '../../shared/api'
import {
  Button,
  Dialog,
  EmptyState,
  Loading,
  Pagination,
  TextInput,
} from '../../shared/components'
import { fmt, useT } from '../../shared/i18n'

import { useMedia } from './hooks'
import { MediaThumb } from './MediaThumb'
import { UploadZone } from './UploadZone'

/** Picks assets from the library (also used by plugin frames through the host bridge). */
export function MediaPicker({
  multiple,
  mimePrefix,
  onClose,
  onPick,
}: {
  multiple?: boolean
  mimePrefix?: string
  onClose: () => void
  onPick: (assets: MediaAsset[]) => void
}) {
  const { t } = useT()
  const [search, setSearch] = useState('')
  const [offset, setOffset] = useState(0)
  const [selected, setSelected] = useState<MediaAsset[]>([])
  const type = mimePrefix?.replace(/\/$/, '')
  const { data, isLoading } = useMedia({ search, type, offset, limit: 24 })

  const toggle = (a: MediaAsset) => {
    if (!multiple) return setSelected([a])
    setSelected((s) =>
      s.some((x) => x.id === a.id) ? s.filter((x) => x.id !== a.id) : [...s, a],
    )
  }

  return (
    <Dialog
      title={multiple ? t.media.selectMedia : t.media.selectFile}
      wide
      onClose={onClose}
      footer={
        <>
          <span className="grow small muted">
            {selected.length
              ? fmt(t.common.selected, { count: selected.length })
              : ''}
          </span>
          <Button variant="ghost" onClick={onClose}>
            {t.common.cancel}
          </Button>
          <Button
            variant="primary"
            disabled={!selected.length}
            onClick={() => onPick(selected)}
          >
            {t.media.useSelected}
          </Button>
        </>
      }
    >
      <div className="stack">
        <UploadZone onUploaded={(a) => toggle(a)} />
        <TextInput
          placeholder={t.media.pickerSearch}
          value={search}
          onChange={(e) => {
            setSearch(e.target.value)
            setOffset(0)
          }}
          aria-label={t.media.searchMedia}
        />
        {isLoading && <Loading />}
        {data && data.items.length === 0 && (
          <EmptyState title={t.media.noneFound} />
        )}
        {data && data.items.length > 0 && (
          <>
            <div className="media-grid">
              {data.items.map((a) => (
                <button
                  key={a.id}
                  className="media-tile"
                  aria-pressed={selected.some((s) => s.id === a.id)}
                  onClick={() => toggle(a)}
                  disabled={a.status === 'FAILED'}
                >
                  <div className="media-tile__thumb">
                    <MediaThumb asset={a} />
                  </div>
                  <div className="media-tile__meta">
                    <div className="media-tile__name">{a.originalFileName}</div>
                    <div className="muted">
                      {a.status === 'READY' ? a.mimeType : a.status}
                    </div>
                  </div>
                </button>
              ))}
            </div>
            <Pagination
              total={data.total}
              limit={data.limit}
              offset={data.offset}
              onChange={setOffset}
            />
          </>
        )}
      </div>
    </Dialog>
  )
}
